import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Title,
  Text,
  Grid,
  Card,
  Metric,
  LineChart,
  BarChart,
  Badge,
} from '@tremor/react';
import { AlertTriangle, Info } from 'lucide-react';
import { getSatisfacaoIndice, extractErrorMessage } from '@/lib/api';

// ── Tipos (payload de GET /satisfacao/indice?days=N) ────────────────────────

interface Pilares {
  p1_realizacao_valor: number;
  p2_satisfacao_explicita: number;
  p3_confianca_aceitacao: number;
  p4_retencao_advocacia: number;
}

interface Indicador {
  chave: string;
  label: string;
  bruto: number | string | null;
  subscore: number | null;
  peso: number | null;
  pontos: number | null;
  informativo: boolean;
}

interface DiaIndice {
  dia: string;
  indice: number;
  provisorio: boolean;
  baixa_amostra: boolean;
  cohort_orgs: number;
  pilares: Pilares;
  indicadores: Indicador[];
}

interface Resumo {
  media_periodo: number | null;
  tendencia: number | null;
  melhor_dia: { dia: string; indice: number } | null;
  pior_dia: { dia: string; indice: number } | null;
}

interface SatisfacaoIndiceData {
  disponivel: boolean;
  erro?: string;
  days: number;
  hoje: string;
  pesos: Record<string, number>;
  serie: DiaIndice[];
  resumo: Resumo;
}

// ── Helpers ─────────────────────────────────────────────────────────────────

const PERIODOS = [7, 14, 30];

function fmtInt(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return new Intl.NumberFormat('pt-BR').format(n);
}

function fmtNum(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return n.toFixed(decimals);
}

function fmtPct(n: number | null | undefined, decimals = 1): string {
  if (n === null || n === undefined) return '—';
  return `${(n * 100).toFixed(decimals)}%`;
}

function fmtDia(iso: string): string {
  const [, m, d] = iso.split('-');
  return `${d}/${m}`;
}

function indiceCor(n: number): 'emerald' | 'amber' | 'red' {
  if (n >= 75) return 'emerald';
  if (n >= 50) return 'amber';
  return 'red';
}

export function SatisfacaoPage() {
  const [days, setDays] = useState(14);
  const [diaSelecionado, setDiaSelecionado] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<SatisfacaoIndiceData>({
    queryKey: ['satisfacao-indice', days],
    queryFn: async () => (await getSatisfacaoIndice(days)).data,
    refetchInterval: 60_000,
  });

  const errMsg = error
    ? extractErrorMessage(error, 'erro ao carregar índice de satisfação')
    : data && !data.disponivel
      ? (data.erro ?? 'dados indisponíveis')
      : null;

  const serie = data?.serie ?? [];
  const resumo = data?.resumo;
  const hojePt = serie.length > 0 ? serie[serie.length - 1] : null;

  const diaAtivo = useMemo(() => {
    if (!serie.length) return null;
    if (diaSelecionado) {
      return serie.find((p) => p.dia === diaSelecionado) ?? serie[serie.length - 1];
    }
    return serie[serie.length - 1];
  }, [serie, diaSelecionado]);

  const indiceChart = useMemo(
    () => serie.map((p) => ({ dia: fmtDia(p.dia), Índice: p.indice })),
    [serie],
  );

  const pilaresChart = useMemo(
    () =>
      serie.map((p) => ({
        dia: fmtDia(p.dia),
        'Realização de valor': p.pilares.p1_realizacao_valor,
        'Satisfação explícita': p.pilares.p2_satisfacao_explicita,
        'Confiança/aceitação': p.pilares.p3_confianca_aceitacao,
        'Retenção/advocacia': p.pilares.p4_retencao_advocacia,
      })),
    [serie],
  );

  const tendenciaLabel =
    resumo?.tendencia === null || resumo?.tendencia === undefined
      ? '—'
      : `${resumo.tendencia > 0 ? '+' : ''}${fmtNum(resumo.tendencia)} pts no período`;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <div className="flex items-center justify-between">
          <Title>Satisfação</Title>
          <div className="flex items-center gap-1 rounded-md border border-slate-700 p-0.5">
            {PERIODOS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setDays(p)}
                className={`rounded px-2.5 py-1 text-xs transition ${
                  days === p
                    ? 'bg-blue-600/30 text-blue-200'
                    : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                }`}
              >
                {p}d
              </button>
            ))}
          </div>
        </div>
        <Text>
          Índice diário de satisfação (0-100). Cohort do dia = orgs de cliente
          com ≥1 processo criado no dia. 4 pilares: Realização de valor (30
          pts) · Satisfação explícita (25 pts) · Confiança/aceitação (20 pts) ·
          Retenção/advocacia (25 pts).
        </Text>
      </header>

      {/* Legenda da fórmula */}
      <Card className="border border-slate-700/40 bg-slate-900/30">
        <div className="flex items-start gap-2">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-300" />
          <div className="grid grid-cols-1 gap-3 text-xs text-slate-300 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="font-medium text-slate-200">
                Realização de valor — 30 pts
              </p>
              <p className="text-slate-400">
                download_rate ×20 + success_rate ×10
              </p>
            </div>
            <div>
              <p className="font-medium text-slate-200">
                Satisfação explícita — 25 pts
              </p>
              <p className="text-slate-400">
                thumbs_ratio ×15 (neutro 0.5 se 0 votos) + entrega_limpa ×10
              </p>
            </div>
            <div>
              <p className="font-medium text-slate-200">
                Confiança/aceitação — 20 pts
              </p>
              <p className="text-slate-400">
                no_reprocess_rate ×10 + aceite_util ×10
              </p>
            </div>
            <div>
              <p className="font-medium text-slate-200">
                Retenção/advocacia — 25 pts
              </p>
              <p className="text-slate-400">
                retorno_d1 ×10 + 2º caso ×8 + compra ×5 + compartilhou ×2
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Hero: índice de hoje */}
      <Grid numItems={1} numItemsLg={3} className="gap-4">
        <Card className="lg:col-span-1 border border-blue-700/40 bg-blue-900/10">
          <Text className="text-blue-200">Índice de hoje</Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && hojePt && (
            <>
              <div className="flex items-center gap-3">
                <Metric className={`text-${indiceCor(hojePt.indice)}-300`}>
                  {fmtNum(hojePt.indice, 1)}
                </Metric>
                <div className="flex flex-col gap-1">
                  {hojePt.provisorio && (
                    <Badge color="amber" size="xs">
                      provisório
                    </Badge>
                  )}
                  {hojePt.baixa_amostra && (
                    <Badge color="red" size="xs" icon={AlertTriangle}>
                      baixa amostra
                    </Badge>
                  )}
                </div>
              </div>
              <Text className="text-xs text-slate-400">
                cohort de {fmtInt(hojePt.cohort_orgs)} orgs ·{' '}
                {hojePt.provisorio
                  ? 'retorno D+1 ainda pode mudar (recompõe amanhã)'
                  : 'índice final'}
              </Text>
              <Text className="pt-1 text-xs text-slate-500">
                tendência do período: {tendenciaLabel}
              </Text>
            </>
          )}
        </Card>

        <Card className="lg:col-span-2">
          <Title>Índice diário ({days}d)</Title>
          <Text className="text-xs text-slate-400">
            Clique num ponto do gráfico para ver o detalhe do dia abaixo.
          </Text>
          {isLoading && <Text>carregando…</Text>}
          {!isLoading && errMsg && (
            <div className="text-sm text-red-400" role="alert">
              {errMsg}
            </div>
          )}
          {!isLoading && !errMsg && (
            <LineChart
              className="mt-3 h-56"
              data={indiceChart}
              index="dia"
              categories={['Índice']}
              colors={['blue']}
              valueFormatter={(v) => fmtNum(v, 0)}
              minValue={0}
              maxValue={100}
              showAnimation
              onValueChange={(v) => {
                if (v && typeof v.dia === 'string') {
                  const found = serie.find((p) => fmtDia(p.dia) === v.dia);
                  if (found) setDiaSelecionado(found.dia);
                }
              }}
            />
          )}
        </Card>
      </Grid>

      {/* Pilares empilhados por dia */}
      <Card>
        <Title>Composição por pilar</Title>
        <Text className="text-xs text-slate-400">
          Barras empilhadas — mostra qual pilar puxou o índice pra cima ou pra
          baixo em cada dia.
        </Text>
        {isLoading && <Text>carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}
        {!isLoading && !errMsg && (
          <BarChart
            className="mt-3 h-64"
            data={pilaresChart}
            index="dia"
            categories={[
              'Realização de valor',
              'Satisfação explícita',
              'Confiança/aceitação',
              'Retenção/advocacia',
            ]}
            colors={['blue', 'emerald', 'violet', 'amber']}
            stack
            valueFormatter={(v) => fmtNum(v, 1)}
            onValueChange={(v: any) => {
              if (v && typeof v.dia === 'string') {
                const found = serie.find((p) => fmtDia(p.dia) === v.dia);
                if (found) setDiaSelecionado(found.dia);
              }
            }}
          />
        )}
      </Card>

      {/* Detalhe do dia — o ponto central do pedido: rastrear origem */}
      <Card>
        <div className="flex items-center justify-between gap-4">
          <div>
            <Title>Detalhe do dia</Title>
            <Text className="text-xs text-slate-400">
              Todos os 20 indicadores: valor bruto, sub-score normalizado,
              peso e pontos contribuídos (ou &quot;informativo&quot; quando
              não entra na fórmula).
            </Text>
          </div>
          <select
            className="rounded-md border border-slate-700 bg-slate-900 px-2 py-1 text-sm text-slate-200"
            value={diaAtivo?.dia ?? ''}
            onChange={(e) => setDiaSelecionado(e.target.value)}
          >
            {serie
              .slice()
              .reverse()
              .map((p) => (
                <option key={p.dia} value={p.dia}>
                  {fmtDia(p.dia)} — {fmtNum(p.indice, 1)}
                  {p.provisorio ? ' (provisório)' : ''}
                </option>
              ))}
          </select>
        </div>

        {isLoading && <Text>carregando…</Text>}
        {!isLoading && errMsg && (
          <div className="text-sm text-red-400" role="alert">
            {errMsg}
          </div>
        )}
        {!isLoading && !errMsg && diaAtivo && (
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Badge color={indiceCor(diaAtivo.indice)} size="lg">
                índice {fmtNum(diaAtivo.indice, 1)}
              </Badge>
              <Text className="text-xs text-slate-400">
                {fmtDia(diaAtivo.dia)} · cohort {fmtInt(diaAtivo.cohort_orgs)}{' '}
                orgs
              </Text>
              {diaAtivo.provisorio && (
                <Badge color="amber" size="xs">
                  provisório — retorno D+1 ainda pode mudar
                </Badge>
              )}
              {diaAtivo.baixa_amostra && (
                <Badge color="red" size="xs" icon={AlertTriangle}>
                  baixa amostra (&lt;5 orgs) — baixa confiança
                </Badge>
              )}
            </div>

            <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-2">
              <Card className="p-3">
                <Text className="text-xs text-slate-400">
                  Realização de valor
                </Text>
                <Metric className="text-lg">
                  {fmtNum(diaAtivo.pilares.p1_realizacao_valor)} / 30
                </Metric>
              </Card>
              <Card className="p-3">
                <Text className="text-xs text-slate-400">
                  Satisfação explícita
                </Text>
                <Metric className="text-lg">
                  {fmtNum(diaAtivo.pilares.p2_satisfacao_explicita)} / 25
                </Metric>
              </Card>
              <Card className="p-3">
                <Text className="text-xs text-slate-400">
                  Confiança/aceitação
                </Text>
                <Metric className="text-lg">
                  {fmtNum(diaAtivo.pilares.p3_confianca_aceitacao)} / 20
                </Metric>
              </Card>
              <Card className="p-3">
                <Text className="text-xs text-slate-400">
                  Retenção/advocacia
                </Text>
                <Metric className="text-lg">
                  {fmtNum(diaAtivo.pilares.p4_retencao_advocacia)} / 25
                </Metric>
              </Card>
            </Grid>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wide text-slate-400">
                    <th className="py-2 pr-4 font-medium">Indicador</th>
                    <th className="py-2 pr-4 font-medium">Bruto</th>
                    <th className="py-2 pr-4 font-medium">Sub-score</th>
                    <th className="py-2 pr-4 font-medium">Peso</th>
                    <th className="py-2 pr-4 font-medium">Pontos</th>
                  </tr>
                </thead>
                <tbody>
                  {diaAtivo.indicadores.map((ind) => (
                    <tr
                      key={ind.chave}
                      className="border-b border-slate-800/50 text-slate-200"
                    >
                      <td className="py-2 pr-4">{ind.label}</td>
                      <td className="py-2 pr-4 font-mono text-xs">
                        {ind.bruto === null ? '—' : String(ind.bruto)}
                      </td>
                      <td className="py-2 pr-4">
                        {ind.subscore === null ? '—' : fmtPct(ind.subscore)}
                      </td>
                      <td className="py-2 pr-4">
                        {ind.peso === null ? '—' : `×${ind.peso}`}
                      </td>
                      <td className="py-2 pr-4 font-medium">
                        {ind.informativo ? (
                          <Badge color="slate" size="xs">
                            informativo
                          </Badge>
                        ) : (
                          fmtNum(ind.pontos, 2)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
